/**
 * Authentication Store (Zustand)
 * Supports admin impersonation
 */
import { create } from 'zustand';
import { api, Client } from '../lib/api';

interface AuthState {
  user: Client | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  originalToken: string | null;

  // Actions
  setAuth: (token: string, user: Client) => void;
  logout: () => void;
  loadUser: () => Promise<void>;
  impersonate: (clientId: string) => Promise<void>;
  stopImpersonating: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: localStorage.getItem('auth_token'),
  isLoading: true,
  isAuthenticated: !!localStorage.getItem('auth_token'),
  isImpersonating: !!localStorage.getItem('impersonating_token'),
  originalToken: localStorage.getItem('admin_original_token'),

  setAuth: (token: string, user: Client) => {
    localStorage.setItem('auth_token', token);
    set({ token, user, isAuthenticated: true, isLoading: false });
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('admin_original_token');
    localStorage.removeItem('impersonating_token');
    set({ token: null, user: null, isAuthenticated: false, isLoading: false, isImpersonating: false, originalToken: null });
  },

  loadUser: async () => {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const user = await api.getCurrentUser();
      const isImpersonating = !!localStorage.getItem('impersonating_token');
      set({ user, token, isAuthenticated: true, isLoading: false, isImpersonating });
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('auth_token');
      set({ token: null, user: null, isAuthenticated: false, isLoading: false });
    }
  },

  impersonate: async (clientId: string) => {
    try {
      const currentToken = localStorage.getItem('auth_token');
      const result = await api.impersonateClient(clientId);

      // Store original admin token
      localStorage.setItem('admin_original_token', currentToken || '');
      localStorage.setItem('impersonating_token', 'true');
      localStorage.setItem('auth_token', result.token);

      set({
        token: result.token,
        user: result.client,
        isImpersonating: true,
        originalToken: currentToken,
      });

      window.location.href = '/';
    } catch (error) {
      console.error('Failed to impersonate:', error);
    }
  },

  stopImpersonating: () => {
    const originalToken = localStorage.getItem('admin_original_token');
    if (originalToken) {
      localStorage.setItem('auth_token', originalToken);
      localStorage.removeItem('admin_original_token');
      localStorage.removeItem('impersonating_token');

      set({
        token: originalToken,
        isImpersonating: false,
        originalToken: null,
      });

      window.location.href = '/admin';
    }
  },
}));
